import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { Message } from 'primeng/message';
import { RadioButtonModule } from 'primeng/radiobutton';
import { AiButtonComponent } from '../../../components/ai-button/ai-button.component';
import {
  BulkCompileService,
  type BulkCompileMode,
} from '../../../services/compiling/bulk-compile.service';
import { AutoCompileStateService } from '../../../services/compiling/state/auto-compile-state.service';

/**
 * The "compila tutte le risposte" trigger and its options step.
 *
 * The trigger, its options step, and the way back into a review the applicant
 * stepped out of. The review dialog itself lives in `BulkCompileStatusComponent`,
 * which the dashboard renders outside the tabs; progress belongs to the global
 * run tracker, since the batch outlives this page.
 */
@Component({
  selector: 'lib-bulk-compile-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AiButtonComponent,
    ButtonModule,
    DialogModule,
    Message,
    RadioButtonModule,
    FormsModule,
  ],
  template: `
    @if (bulk.available()) {
      <div class="flex items-center justify-end gap-2" data-tour="bulk-compile">
        <!-- "Vai alla domanda" closes the review to show the question, so there
             has to be a way back to it. Here rather than in a card docked to the
             page: this is where the run was started and where the applicant
             looks for it. -->
        @if (bulk.pendingReviewCount(); as pending) {
          <p-button
            [label]="
              pending === 1
                ? 'Rivedi 1 risposta'
                : 'Rivedi ' + pending + ' risposte'
            "
            icon="pi pi-list-check"
            severity="secondary"
            [outlined]="true"
            (onClick)="bulk.requestReview()"
          />
        }

        <lib-ai-button
          label="Compila tutte le risposte"
          [loading]="bulk.running()"
          [disabled]="autoCompileState.busy()"
          (action)="openOptions()"
        />
      </div>

      <p-dialog
        header="Compila tutte le risposte"
        [visible]="optionsVisible()"
        (visibleChange)="optionsVisible.set($event)"
        [modal]="true"
        [style]="{ width: '540px' }"
        [draggable]="false"
      >
        <div class="flex flex-col gap-4">
          <p class="m-0 text-sm text-gray-500">
            L'AI genererà una proposta di risposta per le domande compilabili di
            questo formulario ({{ bulk.questionCount() }} in totale), usando i
            documenti caricati e le istruzioni che hai scritto per le singole
            domande.
          </p>

          <div class="flex flex-col gap-3" data-tour="bulk-compile-modes">
            <div class="flex items-start gap-2">
              <p-radiobutton
                [autofocus]="false"
                inputId="bulk-empty-only"
                name="bulkMode"
                value="empty-only"
                [(ngModel)]="mode"
              />
              <label
                for="bulk-empty-only"
                class="flex cursor-pointer flex-col gap-0.5"
              >
                <span class="font-medium">Compila solo le domande vuote</span>
                <span class="text-sm text-gray-500">
                  Le risposte che hai già scritto non vengono toccate.
                </span>
              </label>
            </div>

            <div class="flex items-start gap-2" data-tour="bulk-compile-overwrite">
              <p-radiobutton
                [autofocus]="false"
                inputId="bulk-overwrite"
                name="bulkMode"
                value="overwrite"
                [(ngModel)]="mode"
              />
              <label
                for="bulk-overwrite"
                class="flex cursor-pointer flex-col gap-0.5"
              >
                <span class="font-medium">Rigenera tutte le risposte</span>
                <span class="text-sm text-gray-500">
                  Ogni domanda compilabile viene rigenerata. Vedrai le proposte
                  prima che sostituiscano le risposte attuali.
                </span>
              </label>
            </div>
          </div>

          <p-message severity="warn" styleClass="w-full">
            <span class="text-sm">
              L'operazione può richiedere alcuni minuti e non può essere
              eseguita più di una volta alla volta. Nessuna risposta viene
              salvata automaticamente.
            </span>
          </p-message>
        </div>

        <ng-template pTemplate="footer">
          <div class="flex justify-end gap-2">
            <p-button
              label="Annulla"
              severity="secondary"
              [text]="true"
              (onClick)="optionsVisible.set(false)"
            />
            <p-button
              label="Avvia compilazione"
              icon="pi pi-sparkles"
              data-tour="bulk-compile-start"
              (onClick)="start()"
            />
          </div>
        </ng-template>
      </p-dialog>
    }
  `,
})
export class BulkCompileButtonComponent {
  protected readonly bulk = inject(BulkCompileService);
  protected readonly autoCompileState = inject(AutoCompileStateService);

  protected readonly optionsVisible = signal(false);
  protected mode: BulkCompileMode = 'empty-only';

  protected openOptions(): void {
    this.bulk.clearError();
    this.bulk.dismissOutcome();
    this.optionsVisible.set(true);
  }

  protected start(): void {
    this.optionsVisible.set(false);
    this.bulk.run(this.mode);
  }
}
